import {
  useMutation,
  useQueryClient,
  useQuery,
  QueryKey,
} from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";
import { ErrorMonitoringService } from "@/shared/services";

import {
  CreateScheduledTriggerRequest,
  CreateEventTriggerRequest,
  TriggerType,
  TriggerView,
  TriggerViewConverter,
  UpdateScheduledTriggerRequest,
  UpdateTriggerRequest,
} from "../TriggerRecord";

interface TriggerOptions {
  throwOnError: boolean;
  resourceOwnerId: string;
}

/**
 * One-stop shop for trigger query keys.
 */
class TriggerQueryKeys {
  static singleTriggerByName(triggerName: string | undefined): QueryKey {
    return ["triggerView", triggerName];
  }

  static singleTriggerById(triggerId: string): QueryKey {
    return ["triggerView", triggerId];
  }
}

export function useTrigger(
  name: string | undefined,
  options?: Partial<TriggerOptions>,
) {
  const { triggerService } = useDomainServices();

  return useQuery<TriggerView, Error>({
    queryKey: TriggerQueryKeys.singleTriggerByName(name),
    queryFn({ signal }) {
      if (name === undefined) {
        return Promise.reject(new Error("Invalid trigger name"));
      }

      return triggerService.getTriggerByName(name, {
        abortSignal: signal,
        resourceOwnerId: options?.resourceOwnerId,
      });
    },
    enabled: name !== undefined,
    // 1 Minute until stale
    staleTime: 60000,
    throwOnError: options?.throwOnError ?? false,
  });
}

export interface UpdateTrigger {
  name: string;
  resourceOwnerId: string;
  updates: UpdateTriggerRequest;
}

export function useUpdateTrigger() {
  const { triggerService } = useDomainServices();

  const queryClient = useQueryClient();

  return useMutation<TriggerView, Error, UpdateTrigger>({
    mutationFn: ({ name, resourceOwnerId, updates }) => {
      return triggerService
        .updateTrigger(name, updates, {
          resourceOwnerId,
        })
        .then((record) =>
          TriggerViewConverter.convertEventTriggerRecord(record),
        );
    },

    onSuccess: (triggerView) => {
      queryClient.setQueryData(
        TriggerQueryKeys.singleTriggerByName(triggerView.name),
        triggerView,
      );
    },
  });
}

export interface UpdateScheduledTrigger {
  trigger_id: string;
  updates: UpdateScheduledTriggerRequest;
}

export function useUpdateScheduledTrigger() {
  const { triggerService } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<TriggerView, Error, UpdateScheduledTrigger>({
    mutationFn: ({ trigger_id, updates }) => {
      return triggerService
        .updateScheduledTrigger(trigger_id, updates)
        .then((record) =>
          TriggerViewConverter.convertScheduledTriggerRecord(record),
        );
    },

    onSuccess: (triggerView) => {
      queryClient.setQueryData(
        TriggerQueryKeys.singleTriggerByName(triggerView.name),
        triggerView,
      );
    },
  });
}

interface DeleteTriggerParams {
  name: string;
  resourceOwnerId?: string;
  triggerId: string;
  triggerType?: TriggerType;
}

export function useDeleteTrigger() {
  const { triggerService } = useDomainServices();

  return useMutation<void, Error, DeleteTriggerParams>({
    mutationFn: ({ name, resourceOwnerId, triggerId, triggerType }) => {
      if (triggerType === TriggerType.Scheduled) {
        return triggerService.deleteScheduledTrigger(triggerId);
      } else {
        return triggerService.deleteTrigger(name, { resourceOwnerId });
      }
    },
  });
}

export function useTriggerById(triggerId: string) {
  const { triggerService } = useDomainServices();

  return useQuery<TriggerView, Error>({
    queryKey: TriggerQueryKeys.singleTriggerById(triggerId),
    queryFn: async ({ signal }) => {
      return await triggerService.getTriggerById(triggerId, {
        abortSignal: signal,
      });
    },
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useCreateEventTrigger() {
  const { triggerService } = useDomainServices();
  const { currentOrganization } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateEventTriggerRequest) => {
      if (currentOrganization === null) {
        throw Error("useCreateEventTrigger: current organization is null");
      }

      return triggerService.createEventTrigger(
        request,
        currentOrganization.org_id,
      );
    },
    onError: (error) => {
      ErrorMonitoringService.captureError(error);
    },
    onSuccess: (triggerView) => {
      queryClient.setQueryData(
        TriggerQueryKeys.singleTriggerByName(triggerView.name),
        triggerView,
      );
    },
  });
}

export function useCreateScheduledTrigger() {
  const { triggerService } = useDomainServices();
  const { currentOrganization } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateScheduledTriggerRequest) => {
      if (currentOrganization === null) {
        throw Error("useCreateScheduledTrigger: current organization is null");
      }

      return triggerService.createScheduledTrigger(
        request,
        currentOrganization.org_id,
      );
    },
    onError: (error) => {
      ErrorMonitoringService.captureError(error);
    },
    onSuccess: (triggerView) => {
      queryClient.setQueryData(
        TriggerQueryKeys.singleTriggerByName(triggerView.name),
        triggerView,
      );
    },
  });
}
